import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { MatchConfig, TeamSide, Vec3, WorldSnapshot } from '../../core/contracts';
import { atmosParams, broadcastPost, buildGlows, buildRain, buildSky, buildSnow } from './atmosphere';
import { BALL_RADIUS, buildBall } from './ball';
import { buildCrowd } from './crowd';
import { buildFigures } from './figures';
import { buildGoals, buildPitch } from './pitch';
import { buildStadium } from './stadium';
import { LED_ROW_GOAL_AWAY, LED_ROW_GOAL_HOME, LED_ROWS, blobTexture, ledAtlas } from './textures';
import { StaticBuilder } from './util';

export type WorldQuality = 'low' | 'medium' | 'high';

export interface World {
  scene: THREE.Scene;
  /** Apply a sim snapshot (positions, anims) for this render frame. alpha = interpolation 0..1 between prev and next. */
  update(prev: WorldSnapshot, next: WorldSnapshot, alpha: number, dt: number): void;
  /** World-space positions of named anchors cameras may need: dugouts, corner flags, crowd sections. */
  anchors: Record<string, Vec3>;
  setQuality(q: WorldQuality): void;
  /**
   * Thin the crowd relative to the current quality (0..1). Just an instance-count
   * change, so it's cheap enough to toggle per render (e.g. small monitor-wall tiles).
   */
  setCrowdLod(scale: number): void;
  dispose(): void;
  /**
   * Make the crowd jump/wave. level 0..1; side limits it to one team's fans.
   * Goals are detected automatically from snapshot scores; call this for
   * near-misses, big saves, etc.
   */
  crowdExcite?(level: number, side?: TeamSide): void;
  /** Directional "sun"/floodlight — exposed so the renderer can tweak shadow settings. */
  readonly sun: THREE.DirectionalLight;
  readonly quality: WorldQuality;
}

const QUALITY = {
  low: { crowd: 0.3, rain: 7000, snow: 5000, shadow: 'none', mapSize: 1024 },
  medium: { crowd: 0.62, rain: 15000, snow: 10000, shadow: 'static', mapSize: 2048 },
  high: { crowd: 1.0, rain: 24000, snow: 16000, shadow: 'dynamic', mapSize: 4096 },
} as const;

export function createWorld(config: MatchConfig): World {
  const scene = new THREE.Scene();
  scene.name = 'sports-director-world';
  const disposables: { dispose(): void }[] = [];
  const atm = atmosParams(config.timeOfDay, config.weather);
  const night = config.timeOfDay === 'night';

  // ---------------------------------------------------------------- static world
  const sb = new StaticBuilder();
  const pitch = buildPitch();
  scene.add(pitch.mesh);
  pitch.uniforms.uWet.value = atm.wet;
  pitch.uniforms.uSnow.value = atm.snow;
  pitch.uniforms.uGrassLight.value.copy(atm.grassLight);
  pitch.uniforms.uGrassDark.value.copy(atm.grassDark);
  const nets = buildGoals(sb);
  scene.add(nets);
  const stadium = buildStadium(sb, config);
  const staticGeo = mergeGeometries([stadium.bowlGeometry, sb.build()], false);
  stadium.bowlGeometry.dispose();
  if (!staticGeo) throw new Error('world: static merge failed');
  const staticMat = new THREE.MeshLambertMaterial({ vertexColors: true });
  const staticMesh = new THREE.Mesh(staticGeo, staticMat);
  staticMesh.name = 'stadium-static';
  staticMesh.matrixAutoUpdate = false;
  staticMesh.receiveShadow = true;
  scene.add(staticMesh);

  // LED boards + fascia ribbon (one draw call, animated by texture offset)
  const led = ledAtlas(
    { name: config.home.name, short: config.home.shortName, primary: config.home.primaryColor, secondary: config.home.secondaryColor },
    { name: config.away.name, short: config.away.shortName, primary: config.away.primaryColor, secondary: config.away.secondaryColor });
  // LED panels are emissive: a touch over 1.0 at night so the program bloom catches them.
  const ledBase = night ? 1.35 : 1;
  const ledMat = new THREE.MeshBasicMaterial({ map: led, side: THREE.DoubleSide });
  const ledMesh = new THREE.Mesh(stadium.ledGeometry, ledMat);
  ledMesh.name = 'led-boards';
  ledMesh.matrixAutoUpdate = false;
  scene.add(ledMesh);

  // ---------------------------------------------------------------- crowd
  const crowd = buildCrowd(config, stadium.seats, stadium.seatCount);
  scene.add(crowd.group);
  if (night) {
    // Floodlights are aimed at the pitch: stands sit noticeably darker than the turf.
    staticMat.color.setScalar(0.55);
    crowd.material.color.setScalar(0.62);
    crowd.flagMaterial.color.setScalar(0.7);
  }
  // Per-camera crowd LOD (detailed fans only where they're big on screen).
  const vp = new THREE.Vector4();
  scene.onBeforeRender = (renderer, _scene, camera) => {
    renderer.getCurrentViewport(vp);
    crowd.prepare(camera, vp.w);
  };

  // ---------------------------------------------------------------- figures + ball
  const figures = buildFigures(config);
  for (const m of figures.meshes) scene.add(m);
  const ball = buildBall();
  scene.add(ball);

  // blob shadows (figures + ball) — one draw call
  const blobGeo = new THREE.PlaneGeometry(1, 1);
  blobGeo.rotateX(-Math.PI / 2);
  const blobTex = blobTexture(night);
  const blobMat = new THREE.MeshBasicMaterial({ map: blobTex, transparent: true, depthWrite: false, opacity: atm.blobOpacity, polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -4, fog: true });
  const blobCount = figures.figureCount + 1;
  const blobs = new THREE.InstancedMesh(blobGeo, blobMat, blobCount);
  blobs.name = 'blob-shadows';
  blobs.frustumCulled = false;
  blobs.matrixAutoUpdate = false;
  blobs.renderOrder = 1;
  blobs.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  scene.add(blobs);

  // ---------------------------------------------------------------- sky + lights
  const sky = buildSky();
  const su = sky.uniforms;
  (su.uTop!.value as THREE.Color).copy(atm.skyTop);
  (su.uHorizon!.value as THREE.Color).copy(atm.skyHorizon);
  (su.uGround!.value as THREE.Color).copy(atm.skyGround);
  (su.uSunDir!.value as THREE.Vector3).copy(atm.sunDir);
  (su.uSunColor!.value as THREE.Color).copy(atm.sunColor);
  su.uSunDisk!.value = atm.sunDisk;
  su.uStars!.value = atm.stars;
  su.uClouds!.value = atm.clouds;
  (su.uCloudColor!.value as THREE.Color).copy(atm.cloudColor);
  scene.add(sky.mesh);

  const hemi = new THREE.HemisphereLight(atm.hemiSky, atm.hemiGround, atm.hemiIntensity);
  scene.add(hemi);
  const sun = new THREE.DirectionalLight(atm.sunColor, atm.sunIntensity);
  sun.position.copy(atm.sunDir).multiplyScalar(260);
  sun.target.position.set(0, 0, 0);
  scene.add(sun, sun.target);
  sun.updateMatrixWorld();
  sun.target.updateMatrixWorld();
  const sc = sun.shadow.camera;
  sc.left = -125; sc.right = 125; sc.top = 125; sc.bottom = -125; sc.near = 20; sc.far = 560;
  sc.updateProjectionMatrix();
  sun.shadow.bias = -0.0004;
  sun.shadow.normalBias = 0.04;
  sun.shadow.radius = atm.shadowRadius;
  sun.shadow.intensity = atm.shadowIntensity;
  sun.shadow.autoUpdate = false;
  scene.fog = new THREE.FogExp2(atm.fogColor, atm.fogDensity);
  // Pitch-level floodlight fill so night pitch reads evenly; cheap ambient.
  if (night) scene.add(new THREE.AmbientLight('#9fb0d0', 0.25));

  const glows = buildGlows(stadium.lamps);
  glows.mesh.visible = atm.floodlights;
  scene.add(glows.mesh);
  const rain = buildRain(QUALITY.high.rain);
  rain.mesh.visible = atm.wet > 0;
  scene.add(rain.mesh);
  const snow = buildSnow(QUALITY.high.snow);
  snow.mesh.visible = atm.snow > 0;
  scene.add(snow.mesh);
  // Weather particles on small monitor-wall tiles: a thin hint only (1px streaks at tile size
  // read as blocky noise). Multiview tags each camera with its feed tier.
  let rainCount = 0, snowCount = 0;
  const WALL_PARTICLES = 0.12;
  rain.mesh.onBeforeRender = (_r, _s, cam) => {
    const n = cam.userData.sdTier === 'wall' ? Math.floor(rainCount * WALL_PARTICLES) * 2 : rainCount * 2;
    rain.geometry.setDrawRange(0, n);
  };
  snow.mesh.onBeforeRender = (_r, _s, cam) => {
    snow.geometry.instanceCount = cam.userData.sdTier === 'wall' ? Math.floor(snowCount * WALL_PARTICLES) : snowCount;
  };
  // Program-feed look (bloom + grade), read by the multiview renderer.
  scene.userData.sdPost = broadcastPost(config.timeOfDay, config.weather);

  disposables.push(pitch.mesh.geometry, pitch.material, nets.geometry, nets.material as THREE.Material, staticGeo, staticMat,
    stadium.ledGeometry, ledMat, led, crowd, figures, ball.geometry, ball.material as THREE.Material, blobGeo, blobMat, blobTex,
    sky.mesh.geometry, sky.mesh.material as THREE.Material, glows.geometry, glows.mesh.material as THREE.Material,
    rain.geometry, rain.mesh.material as THREE.Material, snow.geometry, snow.mesh.material as THREE.Material);
  const netMap = (nets.material as THREE.MeshLambertMaterial).map;
  if (netMap) disposables.push(netMap);
  const glowMap = glows.uniforms.uMap.value;
  if (glowMap) disposables.push(glowMap);

  // ---------------------------------------------------------------- shadows / quality
  let quality: WorldQuality = 'medium';
  const staticCasters = [staticMesh];
  const setQuality = (q: WorldQuality) => {
    quality = q;
    const Q = QUALITY[q];
    crowd.setCount(crowd.maxCount * Q.crowd);
    rainCount = Q.rain; snowCount = Q.snow;
    rain.geometry.setDrawRange(0, Q.rain * 2);
    snow.geometry.instanceCount = Q.snow;
    const shadows = Q.shadow !== 'none';
    sun.castShadow = shadows;
    for (const m of staticCasters) m.castShadow = shadows;
    const dyn = Q.shadow === 'dynamic';
    figures.setCastShadow(dyn);
    ball.castShadow = dyn;
    for (const m of figures.meshes) m.receiveShadow = dyn;
    blobs.visible = !dyn;
    if (sun.shadow.mapSize.x !== Q.mapSize) {
      sun.shadow.mapSize.set(Q.mapSize, Q.mapSize);
      if (sun.shadow.map) { sun.shadow.map.dispose(); sun.shadow.map = null; }
    }
    sun.shadow.needsUpdate = true;
  };
  setQuality('medium');

  // ---------------------------------------------------------------- dynamic state
  let time = 0;
  let maxGoals = -1;
  let lastGoalSide: TeamSide | null = null;
  let ledRow = 0, ledHold = 0, ledSlide = 0, ledBaseX = 0;
  const ballQuat = new THREE.Quaternion();
  const tmpQ = new THREE.Quaternion();
  const axis = new THREE.Vector3();
  let lastBallX = NaN, lastBallZ = NaN;
  const m4 = new THREE.Matrix4(), bq = new THREE.Quaternion(), bp = new THREE.Vector3(), bs = new THREE.Vector3();
  const up = new THREE.Vector3(0, 1, 0);
  const zero = new THREE.Matrix4().makeScale(0, 0, 0);
  // shadow stretch from the sun
  const shDir = new THREE.Vector2(-atm.sunDir.x, -atm.sunDir.z);
  if (shDir.lengthSq() > 1e-6) shDir.normalize();
  const tanElev = Math.max(0.2, atm.sunDir.y / Math.max(1e-3, Math.hypot(atm.sunDir.x, atm.sunDir.z)));
  const stretchLen = atm.blobStretch > 0 ? Math.min(6, 1.75 / tanElev) : 0;
  const shAngle = Math.atan2(-shDir.y, shDir.x);

  const crowdExcite = (level: number, side?: TeamSide) => {
    const v = crowd.excite;
    const l = Math.max(0, Math.min(1, level));
    if (!side || side === 'home') v.x = Math.max(v.x, l);
    if (!side || side === 'away') v.y = Math.max(v.y, l);
  };

  const update = (prev: WorldSnapshot, next: WorldSnapshot, alpha: number, dt: number) => {
    dt = Math.max(0, Math.min(0.25, dt));
    alpha = Math.max(0, Math.min(1, alpha));
    time += dt;

    // goals → crowd + LED + bench reactions (ignore replays of already-seen goals)
    const goals = next.score.home + next.score.away;
    if (maxGoals < 0) maxGoals = goals;
    if (goals > maxGoals) {
      lastGoalSide = next.score.home > prev.score.home ? 'home' : next.score.away > prev.score.away ? 'away' : lastGoalSide;
      maxGoals = goals;
      if (lastGoalSide) { crowdExcite(1, lastGoalSide); ledHold = 0; }
    }
    const celebrating: TeamSide | null = next.phase === 'goal_celebration' ? lastGoalSide : null;

    figures.update(prev, next, alpha, dt, celebrating);

    // ball
    const bx = prev.ball.pos.x + (next.ball.pos.x - prev.ball.pos.x) * alpha;
    const by = prev.ball.pos.y + (next.ball.pos.y - prev.ball.pos.y) * alpha;
    const bz = prev.ball.pos.z + (next.ball.pos.z - prev.ball.pos.z) * alpha;
    // ball pos.y is the centre height; clamp so it never sinks into the turf
    const ballY = Math.max(BALL_RADIUS, by);
    if (!Number.isNaN(lastBallX)) {
      const dx = bx - lastBallX, dz = bz - lastBallZ;
      const d = Math.hypot(dx, dz);
      if (d > 1e-5 && d < 6) {
        axis.set(dz, 0, -dx).normalize();
        tmpQ.setFromAxisAngle(axis, d / BALL_RADIUS);
        ballQuat.premultiply(tmpQ);
      }
      const spin = prev.ball.spin + (next.ball.spin - prev.ball.spin) * alpha;
      if (spin) { tmpQ.setFromAxisAngle(up, spin * dt); ballQuat.premultiply(tmpQ); }
      ballQuat.normalize();
    }
    lastBallX = bx; lastBallZ = bz;
    ball.position.set(bx, ballY, bz);
    ball.quaternion.copy(ballQuat);

    // blobs
    if (blobs.visible) {
      const gx = figures.groundX, gz = figures.groundZ, ly = figures.lying;
      for (let i = 0; i < figures.figureCount; i++) {
        if (ly[i]! < 0) { blobs.setMatrixAt(i, zero); continue; }
        const lying = ly[i]!;
        let len: number, w: number, ox = 0, oz = 0, ang = shAngle;
        if (night) { len = 2.6 + lying; w = 2.6 + lying; ang = 0; }
        else if (stretchLen > 0) {
          len = Math.max(0.8 + lying, stretchLen * (1 - lying * 0.6) + 0.5);
          w = 0.7 + lying * 0.5;
          const off = len / 2 - 0.3;
          ox = shDir.x * off; oz = shDir.y * off;
        } else { len = 1.0 + lying; w = 1.0 + lying * 0.3; }
        bp.set(gx[i]! + ox, 0.02, gz[i]! + oz);
        bq.setFromAxisAngle(up, ang);
        bs.set(len, 1, w);
        m4.compose(bp, bq, bs);
        blobs.setMatrixAt(i, m4);
      }
      const h = Math.max(0, ballY - BALL_RADIUS);
      const s = (night ? 0.7 : 0.36) + h * 0.03;
      const shift = stretchLen > 0 ? h / tanElev : 0;
      bp.set(bx + shDir.x * shift, 0.021, bz + shDir.y * shift);
      bq.identity();
      bs.set(s, 1, s);
      m4.compose(bp, bq, bs);
      blobs.setMatrixAt(figures.figureCount, h > 25 ? zero : m4);
      blobs.instanceMatrix.needsUpdate = true;
    }

    // crowd
    const cu = crowd.uniforms;
    cu.uTime.value = time;
    const intensity = prev.intensity + (next.intensity - prev.intensity) * alpha;
    cu.uIntensity.value += (intensity - cu.uIntensity.value) * Math.min(1, dt * 2);
    const decay = Math.exp(-dt / 6);
    const ex = crowd.excite;
    if (celebrating === 'home') ex.x = Math.max(ex.x, 0.85); else ex.x *= decay;
    if (celebrating === 'away') ex.y = Math.max(ex.y, 0.85); else ex.y *= decay;

    // LED boards
    ledHold += dt;
    if (celebrating) {
      ledRow = celebrating === 'home' ? LED_ROW_GOAL_HOME : LED_ROW_GOAL_AWAY;
      ledBaseX += dt * 0.45;
      const flash = 0.75 + 0.45 * (Math.sin(time * 10) * 0.5 + 0.5);
      ledMat.color.setScalar(flash * ledBase);
    } else {
      ledMat.color.setScalar(ledBase);
      if (ledRow >= 6) { ledRow = 0; ledHold = 0; }
      if (ledHold > 9) { ledHold = 0; ledRow = (ledRow + 1) % 6; ledSlide = 1; }
      if (ledSlide > 0) { const step = Math.min(ledSlide, dt * 2); ledSlide -= step; ledBaseX += step * 0.25; }
    }
    led.offset.set(ledBaseX % 1, 1 - (ledRow + 1) / LED_ROWS);

    // weather + sky
    su.uTime!.value = time;
    rain.uniforms.uTime.value = time;
    snow.uniforms.uTime.value = time;

    if (QUALITY[quality].shadow === 'dynamic') sun.shadow.needsUpdate = true;
  };

  const world: World = {
    scene,
    update,
    anchors: stadium.anchors,
    setQuality,
    setCrowdLod(scale: number) {
      crowd.setCount(crowd.maxCount * QUALITY[quality].crowd * Math.max(0, Math.min(1, scale)));
    },
    crowdExcite,
    sun,
    get quality() { return quality; },
    dispose() {
      for (const d of disposables) d.dispose();
      const pu = pitch.uniforms as unknown as Record<string, { value: unknown }>;
      for (const k of ['uDetail', 'uPatch']) (pu[k]?.value as THREE.Texture | undefined)?.dispose();
      if (sun.shadow.map) sun.shadow.map.dispose();
      scene.clear();
    },
  };
  return world;
}
